import { ChevronDown, ChevronUp, History, RefreshCw } from "lucide-react";
import React, { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/shared/Card";
import {
  Box,
  Center,
  HStack,
  SecondaryCTAButton,
  TertiaryCTAButton,
  VStack,
} from "../../components/utils";
import { useEmployeeHistory } from "../../queries/employees";
import {
  EmployeeHistoryChange,
  EmployeeHistoryEvent,
  RelatedEmploymentSummary,
} from "../../types/Employee";
import { formatCurrency, getCurrencyType } from "../../utils/currencyFormatter";

const FIELD_LABELS: Record<string, string> = {
  name: "Employee name",
  legalName: "Legal name",
  status: "Employment status",
  email: "Work email",
  personalEmail: "Personal email",
  hrId: "Employee ID",
  type: "Employee type",
  dateOfJoining: "Date of joining",
  resignationDate: "Resignation date",
  lastDay: "Last working day",
  isDeactivated: "Account status",
  subsidiaryCompanyId: "Organization",
  designation: "Designation",
  department: "Department",
  manager: "Manager",
  managerIds: "Managers",
  "compensationDetails.grade": "Grade",
  "compensationDetails.salary": "Salary",
  contact: "Contact number",
  address: "Address",
  residentialStatus: "Country of residence",
  nationality: "Nationality",
  workingCountry: "Working country",
  note: "Notes",
  "additionalMetaData.tags": "Tags",
  "compensationDetails.dematAccounts": "Demat accounts",
  "compensationDetails.taxPercentage": "Tax slab",
  "compensationDetails.surChargePercentage": "Surcharge percentage",
  "compensationDetails.cessPercentage": "Cess percentage",
  "compensationDetails.isNewRegime": "New tax regime",
  nominees: "Nominees",
  pan: "PAN",
  profilePicture: "Profile photo",
};

function formatTimestamp(value: string) {
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));
}

function formatHistoryDate(value: string) {
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(value));
}

function valueName(value: unknown): string {
  if (value === null || value === undefined || value === "") return "Not set";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return value.toLocaleString("en-IN");
  if (typeof value === "string") return value;
  if (typeof value === "object" && !Array.isArray(value)) {
    const item = value as Record<string, unknown>;
    return String(
      item.name || item.label || item.value || item.id || "Updated"
    );
  }
  return String(value);
}

function valueNames(value: unknown) {
  return Array.isArray(value) ? value.map(valueName) : [];
}

function changedList(oldValue: unknown, newValue: unknown) {
  const oldItems = valueNames(oldValue);
  const newItems = valueNames(newValue);
  return {
    added: newItems.filter((item) => !oldItems.includes(item)),
    removed: oldItems.filter((item) => !newItems.includes(item)),
  };
}

type HistoryRecord = Record<string, unknown>;

type RecordChange = {
  operation: "added" | "removed" | "updated";
  oldValue?: HistoryRecord;
  newValue?: HistoryRecord;
};

function historyRecords(value: unknown): HistoryRecord[] {
  return Array.isArray(value)
    ? value.filter(
        (item): item is HistoryRecord =>
          Boolean(item) && typeof item === "object" && !Array.isArray(item)
      )
    : [];
}

function diffRecords(
  oldValue: unknown,
  newValue: unknown,
  getKey: (record: HistoryRecord) => string,
  areEqual: (oldRecord: HistoryRecord, newRecord: HistoryRecord) => boolean = (
    oldRecord,
    newRecord
  ) => JSON.stringify(oldRecord) === JSON.stringify(newRecord)
): RecordChange[] {
  const oldRecords = historyRecords(oldValue);
  const newRecords = historyRecords(newValue);
  const oldByKey = new Map(
    oldRecords.map((record) => [getKey(record), record])
  );
  const newByKey = new Map(
    newRecords.map((record) => [getKey(record), record])
  );
  const changes: RecordChange[] = [];

  oldByKey.forEach((record, key) => {
    const next = newByKey.get(key);
    if (!next) changes.push({ operation: "removed", oldValue: record });
    else if (!areEqual(record, next)) {
      changes.push({ operation: "updated", oldValue: record, newValue: next });
    }
  });
  newByKey.forEach((record, key) => {
    if (!oldByKey.has(key))
      changes.push({ operation: "added", newValue: record });
  });
  return changes;
}

function displayText(value: unknown) {
  if (value === null || value === undefined || value === "") return "Not set";
  const text = String(value);
  return text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : "Not set";
}

function formatPercentage(value: unknown) {
  const percentage = Number(value);
  if (!Number.isFinite(percentage)) return "Not set";

  const displayValue =
    percentage >= 0 && percentage <= 1 ? percentage * 100 : percentage;
  return `${displayValue.toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}%`;
}

function DetailValue({ label, value }: { label: string; value: unknown }) {
  return (
    <VStack className="min-w-0 gap-1">
      <span className="text-xs text-secondaryText">{label}</span>
      <span className="break-words text-sm font-medium text-primaryText">
        {valueName(value)}
      </span>
    </VStack>
  );
}

function ChangedDetailValue({
  label,
  oldValue,
  newValue,
}: {
  label: string;
  oldValue: unknown;
  newValue: unknown;
}) {
  if (oldValue === newValue) return null;
  return (
    <VStack className="min-w-0 gap-1">
      <span className="text-xs text-secondaryText">{label}</span>
      <HStack className="flex-wrap items-center gap-2 text-sm text-primaryText">
        <span className="break-words">{valueName(oldValue)}</span>
        <span aria-hidden="true" className="text-descriptionColor">
          →
        </span>
        <span className="break-words font-medium">{valueName(newValue)}</span>
      </HStack>
    </VStack>
  );
}

function ChangeOperationLabel({
  operation,
}: {
  operation: RecordChange["operation"];
}) {
  return (
    <span className="rounded-lg bg-tertiaryBg px-2 py-1 text-xs font-medium text-primaryText">
      {displayText(operation)}
    </span>
  );
}

function DematAccountChanges({ change }: { change: EmployeeHistoryChange }) {
  const changes = diffRecords(
    change.oldValue,
    change.newValue,
    (account) =>
      String(account.dematAccountId || account.id || JSON.stringify(account)),
    (oldAccount, newAccount) =>
      ["dematAccountId", "type", "status", "fileName"].every(
        (field) => oldAccount[field] === newAccount[field]
      )
  );
  return (
    <VStack className="gap-3 py-3">
      <p className="text-xs font-medium text-secondaryText">Demat accounts</p>
      {changes.map((accountChange, index) => {
        const account = accountChange.newValue || accountChange.oldValue || {};
        return (
          <VStack
            key={`${String(account.id || account.dematAccountId)}-${index}`}
            className="gap-3 rounded-lg border border-borderColor p-4"
          >
            <HStack className="w-full items-center justify-between gap-3">
              <p className="text-sm font-semibold text-headerColor">
                {account.type ? `${account.type} account` : "Demat account"}
              </p>
              <ChangeOperationLabel operation={accountChange.operation} />
            </HStack>
            {accountChange.operation === "updated" ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <ChangedDetailValue
                  label="Account ID"
                  oldValue={accountChange.oldValue?.dematAccountId}
                  newValue={accountChange.newValue?.dematAccountId}
                />
                <ChangedDetailValue
                  label="Type"
                  oldValue={accountChange.oldValue?.type}
                  newValue={accountChange.newValue?.type}
                />
                <ChangedDetailValue
                  label="Status"
                  oldValue={displayText(accountChange.oldValue?.status)}
                  newValue={displayText(accountChange.newValue?.status)}
                />
                <ChangedDetailValue
                  label="Document"
                  oldValue={accountChange.oldValue?.fileName}
                  newValue={accountChange.newValue?.fileName}
                />
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <DetailValue
                  label="Account ID"
                  value={account.dematAccountId}
                />
                <DetailValue label="Type" value={account.type} />
                <DetailValue
                  label="Status"
                  value={displayText(account.status)}
                />
                {Boolean(account.fileName) && (
                  <DetailValue label="Document" value={account.fileName} />
                )}
              </div>
            )}
          </VStack>
        );
      })}
    </VStack>
  );
}

function NomineeChanges({ change }: { change: EmployeeHistoryChange }) {
  const changes = diffRecords(change.oldValue, change.newValue, (nominee) =>
    String(
      nominee.id ||
        `${nominee.name || ""}:${nominee.relationship || ""}:${
          nominee.dateOfBirth || ""
        }`
    )
  );
  return (
    <VStack className="gap-3 py-3">
      <p className="text-xs font-medium text-secondaryText">Nominees</p>
      {changes.map((nomineeChange, index) => {
        const nominee = nomineeChange.newValue || nomineeChange.oldValue || {};
        return (
          <VStack
            key={`${String(nominee.name)}-${index}`}
            className="gap-3 rounded-lg border border-borderColor p-4"
          >
            <HStack className="w-full items-center justify-between gap-3">
              <p className="text-sm font-semibold text-headerColor">
                {String(nominee.name || "Nominee")}
              </p>
              <ChangeOperationLabel operation={nomineeChange.operation} />
            </HStack>
            {nomineeChange.operation === "updated" ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <ChangedDetailValue
                  label="Relationship"
                  oldValue={nomineeChange.oldValue?.relationship}
                  newValue={nomineeChange.newValue?.relationship}
                />
                <ChangedDetailValue
                  label="Allocation"
                  oldValue={formatPercentage(
                    nomineeChange.oldValue?.percentage
                  )}
                  newValue={formatPercentage(
                    nomineeChange.newValue?.percentage
                  )}
                />
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <DetailValue
                  label="Relationship"
                  value={nominee.relationship}
                />
                <DetailValue
                  label="Allocation"
                  value={formatPercentage(nominee.percentage)}
                />
              </div>
            )}
          </VStack>
        );
      })}
    </VStack>
  );
}

function formattedValue(
  change: EmployeeHistoryChange,
  value: unknown,
  currency: string
) {
  if (change.fieldName === "compensationDetails.salary") {
    return value === null || value === undefined || value === ""
      ? "Not set"
      : formatCurrency(Number(value), currency);
  }
  if (
    ["dateOfJoining", "resignationDate", "lastDay"].includes(
      change.fieldName
    ) &&
    typeof value === "string" &&
    !Number.isNaN(new Date(value).getTime())
  ) {
    return new Intl.DateTimeFormat("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(new Date(value));
  }
  if (Array.isArray(value)) return valueNames(value).join(", ") || "Not set";
  return valueName(value);
}

function ChangeRow({
  change,
  currency,
  isInternalTransferTrigger = false,
}: {
  change: EmployeeHistoryChange;
  currency: string;
  isInternalTransferTrigger?: boolean;
}) {
  const label =
    change.fieldLabel || FIELD_LABELS[change.fieldName] || "Profile field";
  if (change.fieldName === "compensationDetails.dematAccounts") {
    return <DematAccountChanges change={change} />;
  }
  if (change.fieldName === "nominees") {
    return <NomineeChanges change={change} />;
  }
  if (change.fieldName === "pan") {
    return (
      <VStack className="gap-1 py-3">
        <HStack className="items-center gap-2">
          <p className="text-xs font-medium text-secondaryText">{label}</p>
          {isInternalTransferTrigger && <InternalTransferTag />}
        </HStack>
        <p className="text-sm font-medium text-primaryText">PAN was updated</p>
      </VStack>
    );
  }
  if (change.fieldName === "profilePicture") {
    return (
      <VStack className="gap-1 py-3">
        <HStack className="items-center gap-2">
          <p className="text-xs font-medium text-secondaryText">{label}</p>
          {isInternalTransferTrigger && <InternalTransferTag />}
        </HStack>
        <p className="text-sm font-medium text-primaryText">
          {change.newValue === false
            ? "Profile photo was removed"
            : "Profile photo was replaced"}
        </p>
      </VStack>
    );
  }
  if (
    change.fieldName === "managerIds" ||
    change.fieldName === "additionalMetaData.tags"
  ) {
    const { added, removed } = changedList(change.oldValue, change.newValue);
    return (
      <VStack className="gap-1 py-3">
        <HStack className="items-center gap-2">
          <p className="text-xs font-medium text-secondaryText">{label}</p>
          {isInternalTransferTrigger && <InternalTransferTag />}
        </HStack>
        {added.length > 0 && (
          <p className="text-sm text-primaryText">Added: {added.join(", ")}</p>
        )}
        {removed.length > 0 && (
          <p className="text-sm text-primaryText">
            Removed: {removed.join(", ")}
          </p>
        )}
      </VStack>
    );
  }
  return (
    <VStack className="gap-2 py-3">
      <HStack className="items-center gap-2">
        <p className="text-xs font-medium text-secondaryText">{label}</p>
        {isInternalTransferTrigger && <InternalTransferTag />}
      </HStack>
      <HStack className="flex-col items-start gap-1 text-sm text-primaryText sm:flex-row sm:items-center sm:gap-3">
        <span className="min-w-0 break-words">
          {formattedValue(change, change.oldValue, currency)}
        </span>
        <span aria-hidden="true" className="text-descriptionColor">
          →
        </span>
        <span className="min-w-0 break-words font-medium">
          {formattedValue(change, change.newValue, currency)}
        </span>
      </HStack>
    </VStack>
  );
}

function InternalTransferTag() {
  return (
    <span className="rounded-lg bg-tertiaryBg px-2 py-1 text-xs font-medium text-primaryText">
      Internal transfer
    </span>
  );
}

function ActivityEventCard({ event }: { event: EmployeeHistoryEvent }) {
  const [expanded, setExpanded] = useState(event.changes.length <= 4);
  if (event.changeType === "BOOMERANG_REJOIN") {
    return <BoomerangEventCard event={event} />;
  }
  const currency = getCurrencyType();
  const isInternalTransfer = event.changeType === "INTERNAL_TRANSFER";
  const isExcelImport = event.metaData?.source === "EXCEL_IMPORT";
  const internalTransferTriggerFields = new Set(
    isInternalTransfer &&
    Array.isArray(event.metaData?.internalTransferTriggerFields)
      ? event.metaData.internalTransferTriggerFields.filter(
          (field): field is string => typeof field === "string"
        )
      : []
  );
  const visibleChanges = expanded ? event.changes : event.changes.slice(0, 3);
  const countLabel = `${event.changes.length} ${
    event.changes.length === 1 ? "field" : "fields"
  } changed`;
  return (
    <HStack className="items-stretch gap-4">
      <VStack className="items-center gap-0" aria-hidden="true">
        <Center className="h-9 w-9 shrink-0 rounded-full bg-tertiaryBg text-primary">
          <History size={18} />
        </Center>
        <Box className="h-full w-px bg-borderColor" />
      </VStack>
      <Card className="mb-4 min-w-0 flex-1 border-borderColor bg-white shadow-card">
        <CardHeader className="gap-2 border-b border-borderColor p-4 sm:p-6">
          <HStack className="w-full flex-col items-start justify-between gap-2 sm:flex-row">
            <VStack className="min-w-0 gap-1">
              <CardTitle className="text-base leading-6 text-headerColor">
                {isInternalTransfer ? "Internal transfer" : "Profile updated"}
              </CardTitle>
              <p className="text-xs text-secondaryText">
                {isExcelImport ? "Imported from Excel by" : "Updated by"}{" "}
                {event.actor.name} on {formatTimestamp(event.occurredAt)}
                {event.effectiveDate
                  ? ` · Effective from ${formatHistoryDate(
                      event.effectiveDate
                    )}`
                  : ""}
              </p>
            </VStack>
            <span className="rounded-lg bg-tertiaryBg px-3 py-1 text-xs font-medium text-primaryText">
              {countLabel}
            </span>
          </HStack>
        </CardHeader>
        <CardContent className="p-4 sm:p-6">
          <VStack className="divide-y divide-borderColor">
            {visibleChanges.map((change, index) => (
              <ChangeRow
                key={`${change.entity}-${change.fieldName}-${index}`}
                change={change}
                currency={currency}
                isInternalTransferTrigger={internalTransferTriggerFields.has(
                  change.fieldName
                )}
              />
            ))}
          </VStack>
          {event.changes.length > 4 && (
            <TertiaryCTAButton
              className="mt-3 items-center gap-2"
              onClick={() => setExpanded((value) => !value)}
              aria-expanded={expanded}
            >
              {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              {expanded ? "Show fewer changes" : "View all changes"}
            </TertiaryCTAButton>
          )}
        </CardContent>
      </Card>
    </HStack>
  );
}

function BoomerangEventCard({ event }: { event: EmployeeHistoryEvent }) {
  const currency = getCurrencyType();
  const previousChange = event.changes.find(
    (change) => change.fieldName === "previousEmployeeId"
  );
  const rejoinChanges = event.changes.filter(
    (change) => change.fieldName !== "previousEmployeeId"
  );
  const previousEmployee =
    previousChange?.newValue &&
    typeof previousChange.newValue === "object" &&
    !Array.isArray(previousChange.newValue)
      ? (previousChange.newValue as { id?: string; name?: string })
      : undefined;
  const strategy = event.metaData?.resolutionStrategy;
  const isMerge = strategy === "MERGE_EXISTING_EMPLOYEE";
  const isSeparate = strategy === "CREATE_SEPARATE_EMPLOYEE";
  const movedGrants = event.metaData?.moveGrants === true;
  const continuedVesting = event.metaData?.continueVesting === true;
  const movedGrantCount = Number(event.metaData?.movedGrantCount || 0);
  const restoredVestingCount = Number(
    event.metaData?.restoredVestingCount || 0
  );
  const strategyLabel = isMerge
    ? "Merged into the existing employee"
    : isSeparate
    ? "Created a separate linked employee"
    : "Linked employment";
  const grantLabel = movedGrants
    ? `${movedGrantCount} grant(s) moved to this employee`
    : isMerge
    ? "Grants remain on this employee"
    : "Former grants remain on the previous employment";
  const vestingLabel = continuedVesting
    ? `${restoredVestingCount} eligible lapsed tranche(s) restored`
    : "Existing vesting and lapse states preserved";
  return (
    <HStack className="items-stretch gap-4">
      <VStack className="items-center gap-0" aria-hidden="true">
        <Center className="h-9 w-9 shrink-0 rounded-full bg-tertiaryBg text-primary">
          <History size={18} />
        </Center>
        <Box className="h-full w-px bg-borderColor" />
      </VStack>
      <Card className="mb-4 min-w-0 flex-1 border-borderColor bg-white shadow-card">
        <CardHeader className="gap-1 border-b border-borderColor p-4 sm:p-6">
          <CardTitle className="text-base leading-6 text-headerColor">
            {isMerge
              ? "Employee rejoined by merging records"
              : "Employee rejoined with a separate record"}
          </CardTitle>
          <p className="text-xs text-secondaryText">
            Recorded by {event.actor.name} on{" "}
            {formatTimestamp(event.occurredAt)}
            {event.effectiveDate
              ? ` · Effective from ${formatHistoryDate(event.effectiveDate)}`
              : ""}
          </p>
        </CardHeader>
        <CardContent className="p-4 sm:p-6">
          <VStack className="gap-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <VStack className="gap-1">
                <span className="text-xs text-secondaryText">
                  Previous employment
                </span>
                {previousEmployee?.id ? (
                  <Link
                    className="text-sm font-medium text-secondary underline-offset-2 hover:underline focus-visible:ring-2 focus-visible:ring-primary"
                    to={`/options/employeeDetails/${previousEmployee.id}`}
                  >
                    {previousEmployee.name || "View previous employment"}
                  </Link>
                ) : (
                  <span className="text-sm font-medium text-primaryText">
                    {isMerge
                      ? "This employee record was reactivated"
                      : "Previous employment"}
                  </span>
                )}
              </VStack>
              <DetailValue
                label="Rejoined on"
                value={formatHistoryDate(
                  event.effectiveDate || event.occurredAt
                )}
              />
              <DetailValue label="Resolution" value={strategyLabel} />
              <DetailValue label="Grant handling" value={grantLabel} />
              <DetailValue label="Vesting handling" value={vestingLabel} />
            </div>
            {event.note && (
              <VStack className="gap-1 border-t border-borderColor pt-4">
                <span className="text-xs text-secondaryText">Reason</span>
                <p className="text-sm text-primaryText">{event.note}</p>
              </VStack>
            )}
            {rejoinChanges.length > 0 && (
              <VStack className="gap-2 border-t border-borderColor pt-4">
                <HStack className="w-full items-center justify-between gap-3">
                  <p className="text-xs font-medium text-secondaryText">
                    Employee details updated
                  </p>
                  <span className="text-xs text-secondaryText">
                    {rejoinChanges.length}{" "}
                    {rejoinChanges.length === 1 ? "field" : "fields"} changed
                  </span>
                </HStack>
                <VStack className="divide-y divide-borderColor">
                  {rejoinChanges.map((change, index) => (
                    <ChangeRow
                      key={`${change.entity}-${change.fieldName}-${index}`}
                      change={change}
                      currency={currency}
                    />
                  ))}
                </VStack>
              </VStack>
            )}
          </VStack>
        </CardContent>
      </Card>
    </HStack>
  );
}

function RelatedEmploymentCard({
  relationship,
}: {
  relationship: RelatedEmploymentSummary;
}) {
  return (
    <HStack className="items-stretch gap-4">
      <VStack className="items-center gap-0" aria-hidden="true">
        <Center className="h-9 w-9 shrink-0 rounded-full bg-tertiaryBg text-primary">
          <History size={18} />
        </Center>
        <Box className="h-full w-px bg-borderColor" />
      </VStack>
      <Card className="mb-4 min-w-0 flex-1 border-borderColor bg-white shadow-card">
        <CardContent className="p-4 sm:p-6">
          <VStack className="gap-2">
            <p className="text-base font-semibold text-headerColor">
              Rejoined as {relationship.employeeName}
            </p>
            <p className="text-sm text-secondaryText">
              A new employment profile was created on{" "}
              {formatHistoryDate(relationship.occurredAt)}. Former grants remain
              with this employment record.
            </p>
            <Link
              className="w-fit text-sm font-medium text-secondary underline-offset-2 hover:underline focus-visible:ring-2 focus-visible:ring-primary"
              to={`/options/employeeDetails/${relationship.employeeId}`}
            >
              View current employment
            </Link>
          </VStack>
        </CardContent>
      </Card>
    </HStack>
  );
}

function ActivityLogSkeleton() {
  return (
    <VStack className="gap-4" aria-label="Loading employee history">
      {[0, 1, 2].map((item) => (
        <Box
          key={item}
          className="h-36 animate-pulse rounded-lg border border-borderColor bg-white"
        />
      ))}
    </VStack>
  );
}

const ActivityLog = () => {
  const { id = "" } = useParams();
  const historyQuery = useEmployeeHistory(id);
  const events = useMemo(
    () => historyQuery.data?.pages.flatMap((page) => page.items) || [],
    [historyQuery.data]
  );
  const relatedEmployments =
    historyQuery.data?.pages[0]?.relatedEmployments || [];

  return (
    <section aria-labelledby="activity-log-title" className="w-full p-4 sm:p-6">
      <VStack className="gap-6">
        {historyQuery.isLoading && <ActivityLogSkeleton />}

        {historyQuery.isError && (
          <Card className="border-borderColor bg-white shadow-card">
            <CardContent className="p-6">
              <Center className="min-h-40">
                <VStack className="items-center gap-3 text-center">
                  <VStack className="gap-1">
                    <p className="text-base font-semibold text-headerColor">
                      We couldn&apos;t load the activity log
                    </p>
                    <p className="text-sm text-secondaryText">
                      Check your connection and try again
                    </p>
                  </VStack>
                  <SecondaryCTAButton
                    className="items-center gap-2"
                    onClick={() => historyQuery.refetch()}
                  >
                    <RefreshCw size={18} />
                    Try again
                  </SecondaryCTAButton>
                </VStack>
              </Center>
            </CardContent>
          </Card>
        )}

        {!historyQuery.isLoading &&
          !historyQuery.isError &&
          events.length === 0 &&
          relatedEmployments.length === 0 && (
            <Card className="border-borderColor bg-white shadow-card">
              <CardContent className="p-6">
                <Center className="min-h-48">
                  <VStack className="items-center gap-3 text-center">
                    <Center className="h-10 w-10 rounded-full bg-tertiaryBg text-secondaryText">
                      <History size={18} aria-hidden="true" />
                    </Center>
                    <VStack className="gap-1">
                      <p className="text-base font-semibold text-headerColor">
                        No profile changes recorded
                      </p>
                      <p className="max-w-xl text-sm text-secondaryText">
                        Profile changes made after history tracking was enabled
                        will appear here
                      </p>
                    </VStack>
                  </VStack>
                </Center>
              </CardContent>
            </Card>
          )}

        {events.length > 0 && (
          <VStack className="gap-0">
            {relatedEmployments.map((relationship) => (
              <RelatedEmploymentCard
                key={relationship.employeeId}
                relationship={relationship}
              />
            ))}
            {events.map((event) => (
              <ActivityEventCard key={event.id} event={event} />
            ))}
          </VStack>
        )}

        {events.length === 0 && relatedEmployments.length > 0 && (
          <VStack className="gap-0">
            {relatedEmployments.map((relationship) => (
              <RelatedEmploymentCard
                key={relationship.employeeId}
                relationship={relationship}
              />
            ))}
          </VStack>
        )}

        {historyQuery.hasNextPage && (
          <Center>
            <SecondaryCTAButton
              onClick={() => historyQuery.fetchNextPage()}
              disabled={historyQuery.isFetchingNextPage}
            >
              {historyQuery.isFetchingNextPage
                ? "Loading changes..."
                : "Load more"}
            </SecondaryCTAButton>
          </Center>
        )}
      </VStack>
    </section>
  );
};

export default ActivityLog;
